/**
 * 원산지결정기준(PSR) 본문 → 구조화 태그(배지).
 *
 * 관세청 FTA 포털 PSR 원문(한국어) 표현을 유형으로 자동 분류한다. 어디까지나
 * 보조 요약이며, 원문 텍스트는 그대로 노출한다(법적 권위 = 협정문/관세청 원문).
 *
 * 유형:
 *  - WO   완전생산기준 (역내 완전 획득·생산)
 *  - CC   세번변경 2단위(류)  = Change of Chapter
 *  - CTH  세번변경 4단위(호)  = Change of Tariff Heading
 *  - CTSH 세번변경 6단위(소호) = Change of Tariff Sub-Heading
 *  - RVC  부가가치기준 (역내부가가치 비율, 집적/공제법) — % 추출
 *  - SP   특정공정기준 (지정 가공·제조 공정)
 *  - OR   선택 충족 (여러 기준 중 하나)
 *  - AND  동시 충족 (복수 기준 동시)
 *  - COND 조건/예외 (단서 포함)
 */
export function classifyPsr(text) {
  const t = String(text || "");
  if (!t.trim()) return [];
  const tags = [];
  const add = (code, label, tone, title) => {
    if (!tags.some((x) => x.code === code))
      tags.push({ code, label, tone, title });
  };

  // 완전생산
  if (/완전하게\s*획득|완전\s*생산|wholly obtained/i.test(t))
    add("WO", "완전생산", "green", "역내에서 완전히 획득·생산된 것");

  // 세번변경기준 (CTC) — 류/호/소호
  if (/다른\s*류|2단위|류\s*변경|change.*chapter|\bCC\b/i.test(t))
    add(
      "CC",
      "세번변경 2단위(류)",
      "blue",
      "다른 류(2단위)의 재료로부터 생산 = CC",
    );
  if (
    /다른\s*호(?!\s*해설)|모든\s*호[^)]*제외|4단위|호\s*변경|change.*heading|\bCTH\b/i.test(
      t,
    )
  )
    add(
      "CTH",
      "세번변경 4단위(호)",
      "blue",
      "다른 호(4단위)의 재료로부터 생산 = CTH",
    );
  if (/다른\s*소호|6단위|소호\s*변경|subheading|\bCTSH\b/i.test(t))
    add(
      "CTSH",
      "세번변경 6단위(소호)",
      "blue",
      "다른 소호(6단위)의 재료로부터 생산 = CTSH",
    );

  // 부가가치기준 — % 추출 + 방향 판별.
  //   "초과하지 않/이하/not exceed" = 비원산지재료 상한(MaxNOM) → 비원산지재료 ≤N%
  //   "이상/at least" = 역내부가가치 하한(RVC min) → 역내부가가치 ≥N%
  const pct = t.match(/(\d{1,3})\s*(?:퍼센트|%)/);
  const isMaxNOM =
    /초과하지\s*(?:않|아니)|넘지\s*(?:않|아니)|not\s*exceed|이하/i.test(t);
  if (
    /부가가치|역내\s*가치|역내부가|공장도\s*가격|집적법|공제법|\bRVC\b|value of all the materials|ex-works price/i.test(
      t,
    )
  ) {
    let label = "부가가치기준";
    if (pct)
      label = isMaxNOM
        ? `비원산지재료 ≤${pct[1]}%`
        : `역내부가가치 ≥${pct[1]}%`;
    add(
      "RVC",
      label,
      "amber",
      isMaxNOM
        ? `비원산지재료 가치가 공장도가격의 ${pct ? pct[1] : "N"}%를 초과하지 않아야 함(MaxNOM)`
        : "역내부가가치 비율 하한 요건(집적법/공제법) = RVC",
    );
  }

  // 특정공정
  if (
    /특정\s*공정|가공\s*공정|화학\s*반응|정제|방사|제직|specific process/i.test(
      t,
    )
  )
    add("SP", "특정공정", "purple", "지정된 가공·제조 공정 수행 요건");

  // 조합/조건
  if (/또는|이거나|어느\s*하나|중\s*하나|either|\bor\b/i.test(t))
    add("OR", "선택 충족", "gray", "여러 기준 중 하나만 충족하면 됨");
  if (/그리고|동시에|모두\s*충족|및\s/i.test(t))
    add("AND", "동시 충족", "gray", "복수 기준을 동시에 충족해야 함");
  if (/다만|단,|except|provided that/i.test(t))
    add("COND", "조건/예외", "gray", "단서·예외 조건 포함 — 원문 확인 필요");

  return tags;
}

export const PSR_TONES = {
  green: { bg: "#e6f4ea", fg: "#137333", bd: "#9cd2ab" },
  blue: { bg: "#e8f0fe", fg: "#1a56c4", bd: "#a7c2f4" },
  amber: { bg: "#fef7e0", fg: "#8a6100", bd: "#f0d27a" },
  purple: { bg: "#f3e8fd", fg: "#7b2cbf", bd: "#d4b3f0" },
  gray: { bg: "#f1f3f4", fg: "#5f6368", bd: "#dadce0" },
};
